import React, { useEffect, useState, useRef } from 'react';
import { Briefcase, Users, Mail, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const EventimeCareer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="career" className="section" ref={sectionRef}>
      <div className="container">
        {/* Career Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-giant)' }}>
          <h2 
            className={`heading-1 animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              marginBottom: 'var(--spacing-medium)',
              color: 'var(--brand-dark)',
              fontSize: '3rem',
              fontWeight: '800'
            }}
          >
            {t('career.title')}
          </h2>
          
          <p 
            className={`body-large animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '700px', 
              margin: '0 auto',
              transitionDelay: '0.2s',
              color: 'var(--text-secondary)',
              fontSize: '1.2rem'
            }}
          >
            {t('career.subtitle')}
          </p>
        </div>

        {/* Career Options Grid */}
        <div 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: 'var(--spacing-xl)',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          {/* Internship Option */}
          <div 
            className={`animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              transitionDelay: '0.3s'
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-light) 100%)',
              borderRadius: '24px',
              padding: 'var(--spacing-xl)',
              color: 'var(--text-white)',
              boxShadow: 'var(--shadow-heavy)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '500px'
            }}>
              {/* Decorative elements (Z-Index 0) */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                zIndex: 0
              }} />
              
              {/* Content Wrapper (Z-Index 2) */}
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Icon */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  padding: '16px',
                  width: 'fit-content',
                  marginBottom: 'var(--spacing-large)'
                }}>
                  <Users size={32} />
                </div>

                {/* Title */}
                <h3 
                  className="heading-2" 
                  style={{ 
                    marginBottom: 'var(--spacing-medium)',
                    color: 'var(--text-white)',
                    fontWeight: '700'
                  }}
                >
                  {t('career.internship.title')}
                </h3>

                {/* Description */}
                <p 
                  className="body-large" 
                  style={{ 
                    marginBottom: 'var(--spacing-large)',
                    lineHeight: '1.6',
                    color: 'var(--text-white)',
                    opacity: '0.95'
                  }}
                >
                  {t('career.internship.description')}
                </p>

                {/* Benefits */}
                <div style={{ marginBottom: 'var(--spacing-large)' }}>
                  {(t('career.internship.benefits') || []).map && Array.isArray(t('career.internship.benefits')) ? 
                    t('career.internship.benefits').map((benefit, index) => (
                      <div 
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 'var(--spacing-small)',
                          color: 'var(--text-white)'
                        }}
                      >
                        <CheckCircle size={20} style={{ marginRight: 'var(--spacing-small)', color: 'var(--text-white)' }} />
                        <span>{benefit}</span>
                      </div>
                    )) : 
                    <div style={{ color: 'var(--text-white)' }}>
                      {t('career.internship.benefits')}
                    </div>
                  }
                </div>

                {/* Email CTA Text */}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '16px',
                  padding: 'var(--spacing-medium)',
                  marginBottom: 'var(--spacing-medium)'
                }}>
                  <p style={{ 
                    marginBottom: 'var(--spacing-small)',
                    color: 'var(--text-white)',
                    fontWeight: '600'
                  }}>
                    {t('career.internship.instruction')}:
                  </p>
                  <p style={{ 
                    color: 'var(--text-white)',
                    fontWeight: '700',
                    fontSize: '1.1rem'
                  }}>
                    {t('career.internship.email')}
                  </p>
                </div>

                {/* --- FIX TOMBOL KIRIM EMAIL --- */}
                {/* Kita tambah zIndex: 10 supaya dia di atas segalanya */}
                <a
                  href={`mailto:${t('career.internship.email')}`}
                  className="btn-secondary hover-lift"
                  style={{
                    background: 'var(--text-white)',
                    color: 'var(--brand-primary)',
                    border: 'none',
                    padding: '12px 30px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-small)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    width: 'fit-content',
                    position: 'relative', // WAJIB
                    zIndex: 10            // WAJIB (Biar gak ketutupan)
                  }}
                >
                  <Mail size={18} />
                  <span>{t('career.sendEmail')}</span>
                </a>

              </div>
            </div>
          </div>

          {/* Full-Time Option (Disabled Button - No Change Needed) */}
          <div 
            className={`animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              transitionDelay: '0.4s'
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, var(--brand-light) 0%, var(--bg-primary) 100%)',
              borderRadius: '24px',
              padding: 'var(--spacing-xl)',
              border: '3px solid var(--brand-secondary)',
              boxShadow: 'var(--shadow-heavy)',
              position: 'relative',
              overflow: 'hidden',
              minHeight: '500px',
              opacity: '0.8'
            }}>
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '-20px',
                width: '100px',
                height: '100px',
                background: 'rgba(241, 196, 15, 0.1)',
                borderRadius: '50%'
              }} />
              
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{
                  background: 'rgba(44, 62, 80, 0.1)',
                  borderRadius: '16px',
                  padding: '16px',
                  width: 'fit-content',
                  marginBottom: 'var(--spacing-large)',
                  position: 'relative'
                }}>
                  <Briefcase size={32} color="var(--text-secondary)" />
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <XCircle size={24} color="var(--text-light)" />
                  </div>
                </div>

                <h3 
                  className="heading-2" 
                  style={{ 
                    marginBottom: 'var(--spacing-medium)',
                    color: 'var(--text-secondary)',
                    fontWeight: '700'
                  }}
                >
                  {t('career.fulltime.title')}
                </h3>

                <p 
                  className="body-large" 
                  style={{ 
                    marginBottom: 'var(--spacing-large)',
                    lineHeight: '1.6',
                    color: 'var(--text-secondary)'
                  }}
                >
                  {t('career.fulltime.description')}
                </p>

                <div style={{
                  background: 'rgba(133, 146, 158, 0.1)',
                  borderRadius: '16px',
                  padding: 'var(--spacing-large)',
                  textAlign: 'center',
                  border: '2px dashed var(--text-light)'
                }}>
                  <div style={{
                    background: 'var(--text-light)',
                    color: 'var(--text-white)',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    display: 'inline-block',
                    marginBottom: 'var(--spacing-medium)',
                    fontWeight: '700',
                    fontSize: '1.1rem'
                  }}>
                    {t('career.fulltime.status')}
                  </div>
                  
                  <p style={{ 
                    color: 'var(--text-secondary)',
                    lineHeight: '1.6',
                    fontSize: '1rem'
                  }}>
                    {t('career.fulltime.statusDescription')}
                  </p>
                </div>

                <button
                  disabled
                  style={{
                    background: 'var(--text-light)',
                    color: 'var(--text-white)',
                    border: 'none',
                    padding: '12px 30px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderRadius: '25px',
                    cursor: 'not-allowed',
                    opacity: '0.7',
                    marginTop: 'var(--spacing-medium)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-small)'
                  }}
                >
                  <Clock size={18} />
                  <span>{t('career.comingSoon')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div 
          className={`animated ${isVisible ? 'fadeIn' : ''}`}
          style={{ 
            textAlign: 'center', 
            marginTop: 'var(--spacing-giant)',
            transitionDelay: '0.6s'
          }}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, var(--bg-card) 0%, var(--brand-light) 100%)',
              borderRadius: '32px',
              padding: 'var(--spacing-xl)',
              maxWidth: '800px',
              margin: '0 auto',
              boxShadow: 'var(--shadow-medium)',
              border: '2px solid var(--brand-secondary)'
            }}
          >
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                color: 'var(--brand-dark)'
              }}
            >
              {t('career.readyTitle')}
            </h3>
            <p 
              className="body-large" 
              style={{ 
                marginBottom: 'var(--spacing-large)',
                color: 'var(--text-secondary)'
              }}
            >
              {t('career.readyDescription')}
            </p>
            
            {/* --- FIX TOMBOL CONTACT HR --- */}
            {/* Tambah zIndex: 10 juga di sini */}
            <a
              href="mailto:hrd@eventimeofficial.com"
              className="btn-primary hover-lift"
              style={{
                fontSize: '1.1rem',
                padding: '16px 40px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-small)',
                textDecoration: 'none',
                color: 'var(--brand-dark)',
                cursor: 'pointer',
                position: 'relative', // WAJIB
                zIndex: 10            // WAJIB
              }}
            >
              <Mail size={20} />
              <span>{t('career.contactHR')}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventimeCareer;
